<template>
    <div class="md:h-[8rem] flex items-center md:flex-row flex-col gap-6 px-4">
        <div
            class="md:w-[15rem] md:h-auto aspect-video sm:w-full sm:h-20rem rounded-2xl overflow-hidden cursor-pointer bg-black"
            @click="onClick"
        >
            <img class="w-full h-full object-contain" :src="testimonial.getVideoThumbnail()" alt="Image" />
        </div>
        <div class="w-full h-full flex flex-col md:flex-row justify-between py-2 sm:flex-wrap">
            <div class="h-full flex flex-col justify-between">
                <div class="flex flex-row pr-4 items-center gap-2">
                    <div class="grid items-center">
                        <i
                            :class="[
                                'pi',
                                testimonial.isPrivate() ? 'pi-eye-slash' : 'pi-globe',
                                '!text-1xl',
                                'text-gray-500',
                                'pr-2',
                            ]"
                            v-tooltip="testimonial.visibilityToolTip()"
                            style="vertical-align: middle"
                        />
                    </div>
                    <h4 class="text-xl font-semibold text-surface-950 dark:text-surface-0">
                        {{ testimonial.title }}
                    </h4>

                    <Button
                        icon="pi pi-pencil"
                        variant="text"
                        rounded
                        aria-label="Edit"
                        @click="editDialog = !editDialog"
                    />
                </div>

                <div class="grid grid-cols-2 gap-2">
                    <Tag
                        v-if="testimonial.hasAuthor"
                        severity="success"
                        :value="'Author: ' + testimonial.displayName()"
                    />

                    <Tag severity="secondary" :value="'Creation: ' + testimonial.formattedDate()" />

                    <Tag severity="secondary" :value="'Status: ' + testimonial.status" />
                </div>
            </div>
            <div class="flex flex-row md:flex-col sm:row items-end justify-evenly md:justify-center gap-2 pt-2 md:pt-0">
                <Button
                    icon="pi pi-check"
                    severity="contrast"
                    class="w-[9rem]"
                    :label="testimonial.status == TestimonialStatusEnum.processing ? 'Processing' : 'Approve'"
                    :loading="testimonial.status == TestimonialStatusEnum.processing"
                    @click="approveConfirm"
                />

                <Button
                    v-if="testimonial.status == TestimonialStatusEnum.review"
                    icon="pi pi-trash"
                    class="w-[9rem]"
                    label="Discard"
                    severity="secondary"
                    @click="deleteConfirm"
                />
            </div>
        </div>
    </div>

    <ConfirmDialog :draggable="false" :visible="confirmationVisible"></ConfirmDialog>
    <PlayerDialog v-if="visible" v-model:visible="visible" :testimonialId="testimonial.id"> </PlayerDialog>

    <TestimonialDialog
        v-if="editDialog"
        v-model:visible="editDialog"
        :testimonialId="props.testimonial.id"
        @testimonialUpdated="onRefresh"
    >
    </TestimonialDialog>
</template>
<script setup>
import { ref, defineProps, inject, defineEmits } from "vue";
import { Tag, Button } from "primevue";
import PlayerDialog from "@/ui/admin/testimonials/PlayerDialog.vue";
import { TestimonialStatusEnum } from "@/core/enums/TestimonialStatusEnum";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import TestimonialDialog from "../../testimonials/TestimonialDialog.vue";

const emit = defineEmits(["refresh"]);
const testimonialService = inject("testimonialService");

const props = defineProps({
    testimonial: {},
});

const visible = ref(false);
const confirmationVisible = ref(false);
const confirm = useConfirm();
const toast = useToast();
const editDialog = ref(false);

const approveConfirm = () => {
    confirmationVisible.value = true;

    confirm.require({
        message: "This will make it visible for everyone",
        header: "Approve Testimonial?",
        icon: "pi pi-check-circle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Save",
        },
        accept: async () => {
            try {
                await testimonialService.approve(props.testimonial.id);

                emit("refresh");
                confirmationVisible.value = false;

                toast.add({
                    severity: "info",
                    summary: "Testimonial approved!",
                    detail: "The testimonial is ready to be seen",
                    life: 4000,
                });
            } catch (error) {}
        },
        reject: () => {
            confirmationVisible.value = false;
        },
        onHide: () => {
            confirmationVisible.value = false;
        },
    });
};

const deleteConfirm = () => {
    confirmationVisible.value = true;
    confirm.require({
        message: "This can't be undone",
        header: "Delete testimonial?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            try {
                await testimonialService.hardDelete(props.testimonial.id);

                emit("refresh");
                confirmationVisible.value = false;

                toast.add({
                    severity: "info",
                    summary: "Testimonial deleted!",
                    detail: "The testimonial has been permanently deleted.",
                    life: 4000,
                });
            } catch (error) {}
        },
        reject: () => {
            confirmationVisible.value = false;
        },
        onHide: () => {
            confirmationVisible.value = false;
        },
    });
};

const onClick = () => {
    visible.value = true;
};

const onRefresh = () => {
    emit("refresh");
};
</script>
